import { defineHierarchyChart } from '@rdrive/helper-chart-config';
import { ITEMS_CHART, STATUS, ITEM_TEMPLATES, CATEGORIES, issueTemplateShortCircuit } from './itemsHierarchy.js';

// Item breakdown by category -> subcategory -> profile -> subitem -> status, for
// @rdrive/plugin-chart-stacked-bar's StackedHorizontalBarChart. Since IA_ABSORPTION_PLAN Phase 5
// steps 5.2–5.4 a level table on `defineHierarchyChart` (@rdrive/helper-chart-config) — the
// generated queries equal the old hand-written ones (tooling/parity/hierarchy-chart/), and every
// filter value goes through escapeSql (rule 6). Instantiated on the Drawings and Teams (overview)
// pages, so `chartName` must be unique per instance.
//
// `currentLevel` is the page-owned ref bound with v-model:currentLevel — writing to it is how the
// IssueTemplate short-circuit (the profile level's `onSelect`) skips the subitem level. Scoping: the
// drill scope ('andIdIn', ./itemsHierarchy.js) keeps a status click inside the level the user
// navigated into.

const FORM_TEMPLATES = 'JOIN form_templates ft ON i.SubItemTemplateId = ft.id';

export function useCategoryChart({ chartName, filterContext, currentLevel, conn }) {
  return defineHierarchyChart({
    chartName,
    filterContext,
    conn,
    currentLevel,
    ...ITEMS_CHART,
    defaults: { value: 'window' },
    levels: [
      {
        level: 'category', titleKey: 'category',
        id: 'c.id', title: 'c.title', joins: [ITEM_TEMPLATES, CATEGORIES],
        filter: { joins: [ITEM_TEMPLATES], where: [['it.categoryId', 'id']] },
      },
      // A subcategory bar carries its template type (`it.type`) as parentId — the short-circuit reads it
      {
        level: 'subcategory', titleKey: 'subcategory',
        id: 'it.subcategoryId', parentId: 'it.type', title: 'it.subcategoryTitle', joins: [ITEM_TEMPLATES],
        groupBy: 'it.subcategoryId, it.type, it.subcategoryTitle, s.color, s.title, s.sort',
        orderBy: 'it.subcategoryTitle, s.sort, s.title ASC',
        filter: { joins: [ITEM_TEMPLATES], where: [['it.subcategoryID', 'id']] },
      },
      {
        level: 'profile', titleKey: 'profile',
        id: 'it.id', parentId: 'it.code', type: 'i.type', title: 'it.title', joins: [ITEM_TEMPLATES],
        groupBy: 'it.id, it.code, i.type, it.title, s.title, s.color, s.sort',
        partitionBy: 'it.id, it.code, i.type, it.title, s.color, s.sort',
        orderBy: 'it.title, s.sort, s.title ASC',
        onSelect: issueTemplateShortCircuit,
        filter: { where: [['i.profileId', 'id']] },
      },
      {
        level: 'subitem', titleKey: 'subitem',
        id: 'ft.code', parentId: 'ft.code', type: 'i.type', title: 'ft.title', joins: [FORM_TEMPLATES],
        groupBy: 'ft.code, i.type, ft.title, s.title, s.color, s.sort',
        partitionBy: 'ft.code, i.type, ft.title, s.color, s.sort',
        orderBy: 'ft.title, s.sort, s.title ASC',
        filter: { joins: [FORM_TEMPLATES], where: [['ft.code', 'id']] },
      },
      // The status join comes first here, as it always did; the filter is narrowed by the drill scope
      {
        level: 'status', titleKey: 'status',
        id: 's.title', parentId: 'it.code', type: 'i.type', title: 's.title', joins: [STATUS, ITEM_TEMPLATES],
        groupBy: 's.title, it.code, i.type, s.title, s.color', having: 'COUNT(*) > 0', orderBy: 's.title',
        filter: { joins: [STATUS], where: [['s.title', 'id']], scoped: true },
      },
    ],
  });
}
